import { ALL_EDGE_GROUPS } from "./graphWidgetUrlState.js"

export const EDGE_GROUP_LABELS = {
  talks: "Talks",
  scripture: "Scriptures",
  apparatus: "Study helps",
  cfm: "Study helps",
  foundational: "Texts",
}

export const DEFAULT_CONFIDENCE_THRESHOLD = 0.6
export const MIN_CONFIDENCE_THRESHOLD = 0.6
export const MAX_CONFIDENCE_THRESHOLD = 1.0

const SCRIPTURE_CITATION_EDGE_TYPES = new Set([
  "cites_scripture",
  "quotes",
  "alludes_to",
  "echoes",
])

const APPARATUS_NODE_TYPES = new Set([
  "topical_guide_entry",
  "bible_dictionary_entry",
  "triple_index_entry",
])

const FOUNDATIONAL_NODE_TYPES = new Set([
  "foundational_text",
  "foundational_text_section",
])

const STUDY_HELPS_NODE_TYPES = new Set([
  "hymn_text",
])

function nodeTypeFor(nodeById, nodeId) {
  return nodeById.get(nodeId)?.node_type ?? null
}

export function edgeGroupsForEdge(edge, nodeById) {
  const sourceType = nodeTypeFor(nodeById, edge.source)
  const targetType = nodeTypeFor(nodeById, edge.target)
  const groups = []

  if (edge.edge_type === "covers") {
    if (sourceType === "cfm_lesson") groups.push("cfm")
    if (APPARATUS_NODE_TYPES.has(sourceType)) groups.push("apparatus")
  }

  if (edge.edge_type === "variant_of") {
    groups.push("apparatus")
  }

  if (STUDY_HELPS_NODE_TYPES.has(sourceType) || STUDY_HELPS_NODE_TYPES.has(targetType)) {
    groups.push("apparatus")
  }

  if (edge.edge_type === "cites_talk") {
    groups.push("talks")
  }

  if (SCRIPTURE_CITATION_EDGE_TYPES.has(edge.edge_type)) {
    if (sourceType === "scripture_verse" && targetType === "scripture_verse") {
      groups.push("scripture")
    }
    if (sourceType === "conference_talk" || targetType === "conference_talk") {
      groups.push("talks")
    }
    if (FOUNDATIONAL_NODE_TYPES.has(sourceType) || FOUNDATIONAL_NODE_TYPES.has(targetType)) {
      groups.push("foundational")
    }
  }

  if (edge.edge_type === "cites_work") {
    groups.push("foundational")
  }

  return groups
}

function edgePassesConfidence(edge, confidenceThreshold, verifiedOnly) {
  if (verifiedOnly && !edge.verified) return false
  return edge.confidence >= confidenceThreshold
}

function edgePassesGroupFilter(edge, nodeById, activeEdgeGroups) {
  if (edge.link_kind === "personal") return true

  const groups = edgeGroupsForEdge(edge, nodeById)
  if (groups.length === 0) return activeEdgeGroups.length === ALL_EDGE_GROUPS.length

  return groups.some((group) => activeEdgeGroups.includes(group))
}

export function applyGraphFilters(
  payload,
  { edgeGroups, confidenceThreshold, verifiedOnly, keepAllNodes = false }
) {
  if (!payload?.nodes?.length) {
    return { ...payload, nodes: [], edges: [] }
  }

  const nodeById = new Map(payload.nodes.map((node) => [node.id, node]))
  const centerId = payload.center?.id
  const activeEdgeGroups = edgeGroups.length > 0 ? edgeGroups : [...ALL_EDGE_GROUPS]

  const visibleEdges = payload.edges.filter(
    (edge) =>
      edgePassesConfidence(edge, confidenceThreshold, verifiedOnly) &&
      edgePassesGroupFilter(edge, nodeById, activeEdgeGroups)
  )

  if (keepAllNodes) {
    return {
      ...payload,
      nodes: payload.nodes,
      edges: visibleEdges,
    }
  }

  const visibleNodeIds = new Set()
  if (centerId != null) visibleNodeIds.add(centerId)

  visibleEdges.forEach((edge) => {
    visibleNodeIds.add(edge.source)
    visibleNodeIds.add(edge.target)
  })

  return {
    ...payload,
    nodes: payload.nodes.filter((node) => visibleNodeIds.has(node.id)),
    edges: visibleEdges,
  }
}
